import { useState, useEffect, useCallback } from "react";
import { LogOut, RefreshCw, Trash2, Mail, Phone, MessageSquare, Users, UserCheck, Archive, Clock } from "lucide-react";
import { Button } from "../components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const statusMap = {
  new: { label: "Nuovo", color: "bg-blue-100 text-blue-700", icon: Clock },
  contacted: { label: "Contattato", color: "bg-amber-100 text-amber-700", icon: Phone },
  converted: { label: "Convertito", color: "bg-emerald-100 text-emerald-700", icon: UserCheck },
  archived: { label: "Archiviato", color: "bg-slate-100 text-slate-500", icon: Archive },
};

export default function AdminDashboard() {
  const [auth, setAuth] = useState(null);
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [loginError, setLoginError] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState("all");

  const fetchBookings = useCallback(async () => {
    if (!auth) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/bookings`, {
        auth: { username: auth.username, password: auth.password },
      });
      setData(res.data);
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [auth]);

  useEffect(() => {
    if (auth) fetchBookings();
  }, [auth, fetchBookings]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginError("");
    try {
      await axios.get(`${API}/admin/bookings`, {
        auth: { username, password },
      });
      setAuth({ username, password });
    } catch {
      setLoginError("Credenziali non valide");
    }
  };

  const updateStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/admin/bookings/${id}`, { status }, {
        auth: { username: auth.username, password: auth.password },
      });
      fetchBookings();
    } catch (err) {
      console.error(err);
    }
  };

  const deleteBooking = async (id) => {
    if (!window.confirm("Sei sicuro di voler eliminare questa prenotazione?")) return;
    try {
      await axios.delete(`${API}/admin/bookings/${id}`, {
        auth: { username: auth.username, password: auth.password },
      });
      fetchBookings();
    } catch (err) {
      console.error(err);
    }
  };

  const filteredBookings = data?.bookings?.filter(b =>
    filter === "all" ? true : b.status === filter
  ) || [];

  if (!auth) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4" data-testid="admin-login-page">
        <div className="bg-white rounded-2xl shadow-lg border border-slate-200 p-8 w-full max-w-sm">
          <div className="text-center mb-6">
            <img
              src="https://customer-assets.emergentagent.com/job_ai-agency-book/artifacts/uwykpigj_LOGO%20COLIBRI%20VERDE.png"
              alt="Colibri"
              className="h-12 w-auto mx-auto mb-4"
            />
            <h1 className="text-xl font-bold text-slate-900" style={{ fontFamily: 'Outfit' }}>
              Dashboard Admin
            </h1>
            <p className="text-sm text-slate-500 mt-1">Accedi per gestire le prenotazioni</p>
          </div>
          <form onSubmit={handleLogin} className="space-y-4">
            <input
              type="text"
              placeholder="Username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              className="w-full h-11 px-4 rounded-lg border border-slate-200 text-sm focus:outline-none focus:border-[#52B788] focus:ring-1 focus:ring-[#52B788]/20"
              data-testid="admin-username-input"
            />
            <input
              type="password"
              placeholder="Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full h-11 px-4 rounded-lg border border-slate-200 text-sm focus:outline-none focus:border-[#52B788] focus:ring-1 focus:ring-[#52B788]/20"
              data-testid="admin-password-input"
            />
            {loginError && <p className="text-red-500 text-xs" data-testid="admin-login-error">{loginError}</p>}
            <Button
              type="submit"
              className="w-full bg-[#1B4332] hover:bg-[#143728] text-white rounded-lg h-11 font-medium"
              data-testid="admin-login-button"
            >
              Accedi
            </Button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="admin-dashboard">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 md:px-6 h-14 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img
              src="https://customer-assets.emergentagent.com/job_ai-agency-book/artifacts/uwykpigj_LOGO%20COLIBRI%20VERDE.png"
              alt="Colibri"
              className="h-8 w-auto"
            />
            <span className="text-sm font-semibold text-slate-700" style={{ fontFamily: 'Outfit' }}>Admin</span>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={fetchBookings}
              disabled={loading}
              className="text-slate-500"
              data-testid="admin-refresh-button"
            >
              <RefreshCw size={16} className={loading ? "animate-spin" : ""} />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => { setAuth(null); setData(null); }}
              className="text-slate-500"
              data-testid="admin-logout-button"
            >
              <LogOut size={16} />
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 md:px-6 py-8">
        {/* Stats */}
        {data?.stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8" data-testid="admin-stats">
            {[
              { label: "Totali", value: data.stats.total, icon: Users, color: "text-slate-600" },
              { label: "Nuovi", value: data.stats.new, icon: Clock, color: "text-blue-600" },
              { label: "Contattati", value: data.stats.contacted, icon: Phone, color: "text-amber-600" },
              { label: "Convertiti", value: data.stats.converted, icon: UserCheck, color: "text-emerald-600" },
            ].map((s) => (
              <div key={s.label} className="bg-white rounded-xl border border-slate-200 p-5">
                <div className="flex items-center justify-between mb-2">
                  <s.icon size={18} className={s.color} />
                </div>
                <p className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Outfit' }}>{s.value}</p>
                <p className="text-xs text-slate-500 uppercase tracking-wider">{s.label}</p>
              </div>
            ))}
          </div>
        )}

        {/* Filters */}
        <div className="flex items-center gap-2 mb-6 flex-wrap">
          {["all", "new", "contacted", "converted", "archived"].map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`px-4 py-1.5 rounded-full text-xs font-medium transition-colors ${
                filter === f
                  ? "bg-[#1B4332] text-white"
                  : "bg-white border border-slate-200 text-slate-600 hover:bg-slate-50"
              }`}
              data-testid={`filter-${f}`}
            >
              {f === "all" ? "Tutti" : statusMap[f]?.label || f}
            </button>
          ))}
          <span className="text-xs text-slate-400 ml-2">{filteredBookings.length} risultati</span>
        </div>

        {/* Bookings list */}
        <div className="space-y-3">
          {filteredBookings.map((b) => {
            const st = statusMap[b.status] || statusMap.new;
            const StatusIcon = st.icon;
            return (
              <div
                key={b.id}
                className="bg-white rounded-xl border border-slate-200 p-5 hover:shadow-sm transition-shadow"
                data-testid={`booking-item-${b.id}`}
              >
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="font-semibold text-slate-900 text-sm">{b.name}</h3>
                      <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium ${st.color}`}>
                        <StatusIcon size={12} />
                        {st.label}
                      </span>
                    </div>
                    <div className="flex flex-wrap items-center gap-4 text-xs text-slate-500">
                      <span className="flex items-center gap-1"><Mail size={12} /> {b.email}</span>
                      <span className="flex items-center gap-1"><Phone size={12} /> {b.phone}</span>
                      <span>{new Date(b.created_at).toLocaleDateString('it-IT', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</span>
                    </div>
                    {b.message && (
                      <div className="mt-2 flex items-start gap-1.5">
                        <MessageSquare size={12} className="text-slate-400 mt-0.5 flex-shrink-0" />
                        <p className="text-xs text-slate-600 line-clamp-2">{b.message}</p>
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="outline" size="sm" className="text-xs h-8" data-testid={`status-dropdown-${b.id}`}>
                          Cambia stato
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        {Object.entries(statusMap).map(([key, val]) => (
                          <DropdownMenuItem key={key} onClick={() => updateStatus(b.id, key)}>
                            <val.icon size={14} className="mr-2" />
                            {val.label}
                          </DropdownMenuItem>
                        ))}
                      </DropdownMenuContent>
                    </DropdownMenu>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-red-400 hover:text-red-600 hover:bg-red-50 h-8 w-8 p-0"
                      onClick={() => deleteBooking(b.id)}
                      data-testid={`delete-booking-${b.id}`}
                    >
                      <Trash2 size={14} />
                    </Button>
                  </div>
                </div>
              </div>
            );
          })}

          {filteredBookings.length === 0 && (
            <div className="text-center py-16">
              <Users size={40} className="text-slate-200 mx-auto mb-3" />
              <p className="text-slate-400 text-sm">Nessuna prenotazione trovata</p>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
